import { useCallback, useMemo, useState } from 'react'

import { useUserData } from 'components/contexts/UserDataContext'
import constate from 'constate'
import { useTranslation } from 'react-i18next'
import { isPostalCode, isVAT } from 'utils/forms'
import { getDefaultErrorMessage } from 'utils/forms/getDefaultErrorMessage'

/**
 * Structure of the invoicing address form fields.
 */
export interface InvoicingAddressFields {
  /** User-defined label for the address (e.g., "Head Office"). */
  addressName: string
  /** Company or legal entity name. */
  companyOrIndividualName: string
  /** Street and building number. */
  streetAndNumber: string
  /** Apartment, suite, or unit number (optional). */
  apartmentNumber: string
  /** Administrative region, province, or state. */
  region: string
  /** Country name or ISO code. */
  country: string
  /** City, town, or locality. */
  city: string
  /** Postal or ZIP code. */
  postalCode: string
  /** VAT identification number. */
  vatNumber: string
}

/**
 * Props accepted by the {@link InvoicingAddressFormProvider}.
 */
interface Props {
  /**
   * Optional partial values used to prefill the form on mount.
   * When provided, they override any internally derived defaults.
   */
  defaultValues?: Partial<InvoicingAddressFields>
}

/**
 * React context and hook pair that manage the invoicing address form state.
 *
 * @remarks
 * Uses the `constate` library to create both the context provider and its
 * associated consumer hook. Provides access to:
 * - Current field values and errors
 * - Validation logic
 * - Form reset and submit helpers
 *
 * @returns
 * A tuple `[InvoicingAddressFormProvider, useInvoicingAddressForm]` where:
 * - `InvoicingAddressFormProvider` wraps children with form context.
 * - `useInvoicingAddressForm` returns the form API.
 *
 * @example
 * ```tsx
 * <InvoicingAddressFormProvider defaultValues={{ companyName: 'Acme Ltd.' }}>
 *   <MyFormComponent />
 * </InvoicingAddressFormProvider>
 *
 * const { fields, isValid, getFormValues } = useInvoicingAddressForm();
 * ```
 */
export const [InvoicingAddressFormProvider, useInvoicingAddressForm] = constate(({ defaultValues }: Props) => {
  const { t } = useTranslation(['country'])
  const { baseUserData } = useUserData()

  /**
   * Combines default empty values, user country from context,
   * and optional `defaultValues` into one stable object.
   */
  const _initialValues = useMemo(
    () => ({
      addressName: '',
      companyOrIndividualName: '',
      streetAndNumber: '',
      apartmentNumber: '',
      region: '',
      country: baseUserData?.country ?? '',
      city: '',
      postalCode: '',
      vatNumber: '',
      ...(defaultValues ?? {}),
    }),
    [baseUserData?.country, defaultValues]
  )

  /** Current form field values. */
  const [values, setValues] = useState<InvoicingAddressFields>(_initialValues)

  /** Map of field-specific validation error messages. */
  const [errors, setErrors] = useState<Partial<Record<keyof InvoicingAddressFields, string>>>({})

  /** List of required field keys for validation. */
  const requiredFields: (keyof InvoicingAddressFields)[] = useMemo(
    () => ['addressName', 'companyOrIndividualName', 'streetAndNumber', 'country', 'city', 'postalCode'],
    []
  )

  /**
   * Validates a single field by name and updates the `errors` state.
   *
   * @param name - The field key to validate.
   * @param value - The raw input value.
   * @returns The error message (empty string if valid).
   */
  const validateField = useCallback(
    (name: keyof InvoicingAddressFields, value: string) => {
      let error = ''

      if (requiredFields.includes(name) && !value.trim()) {
        error = getDefaultErrorMessage('required')
      }

      if (name === 'postalCode' && value) {
        error = isPostalCode({ value: value.trim() }) ?? ''
      }

      if (name === 'vatNumber' && value) {
        error = isVAT({ value: value.trim(), countryCode: values.country }) ?? ''
      }

      setErrors(prev => ({ ...prev, [name]: error }))
      return error
    },
    [requiredFields, values.country]
  )

  /**
   * Returns a change handler for controlled input fields.
   *
   * @param name - Field key.
   * @returns Change event handler bound to the field.
   */
  const handleChange = useCallback(
    (name: keyof InvoicingAddressFields) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setValues(prev => {
          const next = { ...prev, [name]: value }
          // Re-validate VAT format when country changes
          if (name === 'country' && prev.vatNumber) {
            const vatError = isVAT({ value: prev.vatNumber.trim(), countryCode: value }) ?? ''
            setErrors(prevErrors => ({ ...prevErrors, vatNumber: vatError }))
          }
          return next
        })
        validateField(name, value)
      },
    [validateField]
  )

  /**
   * Boolean indicating if the entire form passes validation.
   * Checks for missing required fields and any stored errors.
   */
  const isValid = useMemo(() => {
    const hasErrors = Object.values(errors).some(e => e)
    const hasEmptyRequired = requiredFields.some(
      key => !values[key]?.trim()
    )
    return !hasErrors && !hasEmptyRequired
  }, [errors, values, requiredFields])

  /**
   * Returns the current form values snapshot.
   *
   * @returns The latest `InvoicingAddressFields` object.
   */
  const getFormValues = useCallback(() => values, [values])

  /**
   * Field configurations, memoized for performance.
   * Each entry contains value, error, and change handler.
   */
  const fields = useMemo(
    () => ({
      addressName: { value: values.addressName, error: errors.addressName, onChange: handleChange('addressName') },
      companyOrIndividualName: { value: values.companyOrIndividualName, error: errors.companyOrIndividualName, onChange: handleChange('companyOrIndividualName') },
      streetAndNumber: { value: values.streetAndNumber, error: errors.streetAndNumber, onChange: handleChange('streetAndNumber') },
      apartmentNumber: { value: values.apartmentNumber, error: errors.apartmentNumber, onChange: handleChange('apartmentNumber') },
      region: { value: values.region, error: errors.region, onChange: handleChange('region') },
      country: { value: values.country, error: errors.country, onChange: handleChange('country') },
      city: { value: values.city, error: errors.city, onChange: handleChange('city') },
      postalCode: { value: values.postalCode, error: errors.postalCode, onChange: handleChange('postalCode') },
      vatNumber: { value: values.vatNumber, error: errors.vatNumber, onChange: handleChange('vatNumber') },
    }),
    [values, errors, handleChange]
  )

  /**
   * Fills the postal fields from an address-search selection and validates what it filled.
   * Only non-empty components are applied — Google can omit any of them, and a selection must
   * never wipe a value the user already typed. Country is deliberately untouched: it is fixed
   * to the account country for non-admins, and a foreign search result must not flip the
   * billing country silently.
   */
  const applyPlace = useCallback(
    (place: { streetAndNumber: string, postalCode: string, city: string }) => {
      const filled: Partial<Pick<InvoicingAddressFields, 'streetAndNumber' | 'postalCode' | 'city'>> = {
        ...(place.streetAndNumber && { streetAndNumber: place.streetAndNumber }),
        ...(place.postalCode && { postalCode: place.postalCode }),
        ...(place.city && { city: place.city }),
      }
      setValues(prev => ({ ...prev, ...filled }))
      Object.entries(filled).forEach(([name, value]) =>
        validateField(name as keyof InvoicingAddressFields, value)
      )
    },
    [validateField]
  )

  /**
   * Resets the form to its initial default values and clears all errors.
   */
  const reset = useCallback(() => {
    setErrors({})
    setValues(_initialValues)
  }, [_initialValues])

  return {
    fields,
    errors,
    isValid,
    getFormValues,
    applyPlace,
    reset,
  }
})
